import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import BrandLogo from "../components/BrandLogo";
import { trackEvent, trackPageView } from "../lib/analytics";
import { 
  BarChart3, 
  FileText, 
  Users, 
  TrendingUp, 
  CheckCircle2, 
  ArrowRight,
  Target,
  Shield,
  Lightbulb,
  MessageCircleMore
} from "lucide-react";

const LandingPage = () => {
  const [supportConfig, setSupportConfig] = useState({
    phone: "5586999613119",
    message: "ESTOU COM DÚVIDAS SOBRE O DIAGNÓSTICO E PRECISO DE AJUDA",
  });

  useEffect(() => {
    const loadSupportConfig = async () => {
      try {
        const response = await axios.get(`${API}/public/support-config`);
        setSupportConfig({
          phone: response.data?.phone || "5586999613119",
          message: response.data?.message || "ESTOU COM DÚVIDAS SOBRE O DIAGNÓSTICO E PRECISO DE AJUDA",
        });
      } catch (error) {
        // Keep the default support shortcut available even if the public config
        // endpoint is temporarily unavailable.
      }
    };
    loadSupportConfig();
    trackPageView("landing_view", { path: "/" });
  }, []);

  const supportHref = useMemo(() => {
    const phone = String(supportConfig.phone || "").replace(/\D/g, "") || "5586999613119";
    const message = encodeURIComponent(
      supportConfig.message || "ESTOU COM DÚVIDAS SOBRE O DIAGNÓSTICO E PRECISO DE AJUDA"
    );
    return `https://wa.me/${phone}?text=${message}`;
  }, [supportConfig]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandLogo size="md" className="max-w-[220px]" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button
                variant="ghost"
                data-testid="nav-login-btn"
                className="text-navy hover:bg-navy/5"
                onClick={() => trackEvent("login_cta_click", { path: "/", metadata: { placement: "nav" } })}
              >
                Entrar
              </Button>
            </Link>
            <Link to="/register">
              <Button
                data-testid="nav-register-btn"
                className="bg-navy hover:bg-navy/90 text-white"
                onClick={() => trackEvent("register_cta_click", { path: "/", metadata: { placement: "nav" } })}
              >
                Criar Conta
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1766198971236-643fa8871b21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzcxNzA1MDIxfDA&ixlib=rb-4.1.0&q=85')" }}
        >
          <div className="hero-overlay absolute inset-0"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <span className="inline-block px-4 py-2 bg-gold/15 text-gold-50 rounded-sm text-sm font-medium mb-6 border border-gold/20">
              Diagnóstico Empresarial Credita CGE
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-6">
              Diagnóstico Empresarial Credita CGE
            </h1>
            <p className="text-lg text-slate-200 mb-8 max-w-xl">
              Avalie 13 áreas estratégicas do seu negócio, receba um relatório objetivo e enxergue rapidamente onde agir para melhorar gestão, desempenho e resultado.
            </p>
            {/* Pricing block keeps the existing CTA flow intact while signaling the new paid offer. */}
            <div className="mb-8 max-w-xl rounded-sm border border-white/10 bg-white/8 p-5 backdrop-blur-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-gold-100/90">Oferta de Lançamento</p>
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <div>
                  <p className="text-sm text-slate-300">De:</p>
                  <p className="text-2xl text-slate-400 line-through">R$ 97,00</p>
                </div>
                <div>
                  <p className="text-sm text-slate-200">Por:</p>
                  <p className="text-4xl font-bold text-white">R$ 19,90</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-200">
                Acesso ao diagnóstico empresarial com relatório inicial e recomendações práticas.
              </p>
              <div className="mt-4 rounded-sm border border-gold/30 bg-gold/12 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-50">Bônus Incluído</p>
                <p className="mt-1 text-base font-semibold text-white">
                  Avaliação de DRE/Balanço
                </p>
                <p className="mt-1 text-sm text-slate-200">
                  Envie seus relatórios financeiros e receba uma leitura inicial com apoio de IA.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button 
                  data-testid="hero-cta-btn"
                  className="btn-gold text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                  onClick={() => trackEvent("register_cta_click", { path: "/", metadata: { placement: "hero" } })}
                >
                  Garantir Diagnóstico por R$ 19,90
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                Saiba Mais
              </Button>
            </div>
          </div>
          
          <div className="col-span-12 lg:col-span-5 hidden lg:block">
            <Card className="brand-surface backdrop-blur-sm shadow-2xl border border-white/40">
              <CardContent className="p-8">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-6">Áreas Avaliadas</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    "Financeiro",
                    "Planejamento",
                    "Comercial",
                    "RH",
                    "Administração",
                    "Produção",
                    "Logística",
                    "Tecnologia/IA",
                    "Jurídico",
                    "Marketing",
                    "Compras",
                    "NR-1",
                    "Visão de Futuro"
                  ].map((area, i) => {
                    const isNr1 = area === "NR-1";
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 text-sm ${
                          isNr1 ? "bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 text-amber-900 font-semibold" : "text-slate-600"
                        }`}
                      >
                        <CheckCircle2 className={`h-4 w-4 flex-shrink-0 ${isNr1 ? "text-amber-600" : "text-emerald-500"}`} />
                        <span>{area}</span>
                        {isNr1 && (
                          <span className="ml-1 text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 rounded px-1.5 py-0.5">
                            Destaque 2026
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mb-4">
              Por que fazer o Diagnóstico?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Identifique oportunidades de melhoria e tome decisões estratégicas baseadas em dados.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-hover border-0 shadow-md">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-navy/10 rounded-lg flex items-center justify-center mb-6">
                  <BarChart3 className="h-7 w-7 text-navy" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-navy mb-3">
                  Análise Completa
                </h3>
                <p className="text-slate-600">
                  61 perguntas em 13 áreas estratégicas para uma visão 360° do seu negócio.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-hover border-0 shadow-md">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gold/15 rounded-lg flex items-center justify-center mb-6">
                  <FileText className="h-7 w-7 text-gold-700" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-navy mb-3">
                  Relatório Detalhado
                </h3>
                <p className="text-slate-600">
                  PDF profissional com gráfico radar, notas por área e recomendações práticas.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-hover border-0 shadow-md">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                  <Users className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-navy mb-3">
                  Consultoria Especializada
                </h3>
                <p className="text-slate-600">
                  Conecte-se com consultores especializados nas áreas que mais precisam de atenção.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mb-4">
              Como Funciona
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Target, title: "1. Responda", desc: "Responda às 61 perguntas sobre sua empresa" },
              { icon: TrendingUp, title: "2. Analise", desc: "O sistema calcula suas notas automaticamente" },
              { icon: Lightbulb, title: "3. Receba", desc: "Baixe seu relatório com gráficos e dicas" },
              { icon: Shield, title: "4. Evolua", desc: "Conecte-se com consultores especializados" }
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-navy mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">
            Pronto para fazer o diagnóstico da sua empresa?
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Aproveite o valor promocional e receba um retrato inicial da sua gestão.
          </p>
          <Link to="/register">
            <Button 
              data-testid="cta-bottom-btn"
              className="btn-gold text-white font-semibold px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Comprar Diagnóstico
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <BrandLogo size="sm" className="brightness-[1.18] contrast-[1.02]" />
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} Credita CGE. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      <a
        href={supportHref}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-3 rounded-full bg-emerald-500 px-4 py-3 text-white shadow-xl transition-all hover:bg-emerald-600 hover:shadow-2xl"
      >
        <MessageCircleMore className="h-5 w-5" />
        <span className="hidden sm:inline text-sm font-medium">Precisa de ajuda?</span>
      </a>
    </div>
  );
};

export default LandingPage;
